import { EV } from '../core/eventbus.js';
import { Repo } from '../data/schema.js';

/** 1 现实秒 = 1 游戏分钟，一整天约 24 分钟 */
export const MINUTES_PER_SECOND = 1;
const DAY = 1440;

/** 昼夜亮度曲线：0 全黑，1 全亮 */
export function daylightAt(minute) {
  const m = ((minute % DAY) + DAY) % DAY;
  if (m < 300) return 0.18;                                  // 00:00-05:00 深夜
  if (m < 420) return 0.18 + (m - 300) / 120 * 0.82;         // 05:00-07:00 拂晓
  if (m < 1020) return 1;                                    // 07:00-17:00 白日
  if (m < 1170) return 1 - (m - 1020) / 150 * 0.82;          // 17:00-19:30 黄昏
  return 0.18;                                               // 19:30-24:00 入夜
}

export function formatClock(minute) {
  const m = Math.floor(((minute % DAY) + DAY) % DAY);
  const hh = String(Math.floor(m / 60)).padStart(2, '0');
  const mm = String(m % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function phaseName(minute) {
  const m = ((minute % DAY) + DAY) % DAY;
  if (m < 300) return '夜半';
  if (m < 420) return '拂晓';
  if (m < 540) return '清晨';
  if (m < 660) return '上午';
  if (m < 780) return '正午';
  if (m < 1020) return '午后';
  if (m < 1170) return '黄昏';
  return '入夜';
}

export const SEASONS = { spring: '春', summer: '夏', autumn: '秋', winter: '冬' };

/** 三十天一换季：春 -> 夏 -> 秋 -> 冬。由天数直接推出来，重复跑也不会错 */
const SEASON_ORDER = ['spring', 'summer', 'autumn', 'winter'];
export function seasonAt(day) {
  const i = Math.floor((Math.max(1, Number(day) || 1) - 1) / 30) % 4;
  return SEASON_ORDER[i];
}
export function seasonName(day) { return SEASONS[seasonAt(day)]; }

/**
 * 世界时间系统：推进时刻、跨日结算、昼夜亮度、属性自然消耗。
 */
export const timeSystem = {
  name: 'time',
  _acc: 0,
  _drain: 0,
  update(dt, ctx) {
    const { db, engine, events } = ctx;
    const w = Repo.getWorld(db);
    const before = w.day;

    w.minute += dt * MINUTES_PER_SECOND;
    while (w.minute >= DAY) {
      w.minute -= DAY;
      w.day += 1;
      w.dayCount += 1;
      onNewDay(db, events, w);
    }

    // 属性自然消耗（按游戏分钟）
    this._drain += dt * MINUTES_PER_SECOND;
    if (this._drain >= 10) {
      const n = this._drain / 10;
      this._drain = 0;
      const p = Repo.getPlayer(db);
      const night = daylightAt(w.minute) < 0.5;
      Repo.savePlayer(db, {
        satiety: Math.max(0, p.satiety - 0.6 * n),
        energy: night ? Math.min(100, p.energy + 0.4 * n) : Math.max(0, p.energy - 0.25 * n),
      });
    }

    // 昼夜
    if (engine.renderer) engine.renderer.view.daylight = daylightAt(w.minute);

    // 定期标脏，交给数据库节流落盘
    this._acc += dt;
    if (this._acc >= 3) {
      this._acc = 0;
      db._touch();
      events.emit(EV.TIME_TICK, { day: w.day, minute: w.minute });
    }

    if (w.day !== before) events.emit(EV.TIME_NEWDAY, { day: w.day });
    db.tick(dt);
  },
};

function onNewDay(db, events, w) {
  // 时辰 -> 四时：换季是天数推出来的，同一天跑几遍都一样
  const season = seasonAt(w.day);
  if (w.season !== season) {
    w.season = season;
    Repo.saveWorld(db, { season });
    Repo.log(db, `入${SEASONS[season]}了。`, 'day', w.day, 0);
  }
  // 菜畦与水田成长：浇过水的才长；夏天长得快，冬天干脆歇着
  const fast = { spring: 0.2, summer: 0.35, autumn: 0.1, winter: 0 }[season] || 0;
  const props = db.table('props');
  for (const p of props.all()) {
    if (p.type !== 'plot' && p.type !== 'paddy_plot') continue;
    const st = p.state || { stage: 0, watered: false };
    if (season === 'winter') continue;                 // 地里歇着，水先替它存着
    if (st.watered && st.stage > 0 && st.stage < 3) {
      const grow = 1 + (Math.random() < fast ? 1 : 0);
      props.update(p.id, { state: { stage: Math.min(3, st.stage + grow), watered: false } });
    } else if (st.watered) {
      props.update(p.id, { state: { ...st, watered: false } });
    }
  }
  // 果树恢复
  for (const p of props.whereEq('type', 'tree')) {
    if (p.state && p.state.fruit === false) {
      if (Math.random() < 0.6) props.update(p.id, { state: { fruit: true } });
    }
  }
  Repo.log(db, `第 ${w.day} 天开始了。`, 'day', w.day, 0);
  events.emit(EV.TIME_NEWDAY, { day: w.day });
}
